import _ from 'lodash'
import { EnrollmentState } from 'src/graphql.generated'
import MeruDate from 'src/legacy/lib/MeruDate'
import type { Program } from 'src/legacy/models/firebase'
import { getThemeLabel } from 'src/participant/ParticipantInfo/programThemeLabel'
import { HomeViewParticipantFragment } from './searchParticipants.generated'

export const HOME_VIEW_ENROLLMENT_STATES = [
  { enrollmentState: EnrollmentState.IntakeRegistered, label: 'Registered' },
  { enrollmentState: EnrollmentState.IntakeLoggedIn, label: 'App Logged in' },
  { enrollmentState: EnrollmentState.IntakeCallScheduled, label: 'Call Scheduled' },
  { enrollmentState: EnrollmentState.InProgram, label: 'In Program' },
]

export type ProgramsByID = Record<string, Pick<Program, 'lastWeekNbr' | 'weeks' | 'providerRole'>> | null | undefined

const DEFAULT_LAST_WEEK_NBR = 12

// Module sort values: onboarding first, then in-program modules 1..N by weekNbr, then wrapping up last.
export const ONBOARDING_MODULE = { key: 'onboarding', label: 'Onboarding', sortValue: -1 } as const
export const WRAPPING_UP_MODULE = {
  key: 'wrapping-up',
  label: 'Wrapping up',
  sortValue: Number.MAX_SAFE_INTEGER,
} as const

// Themes inside the Onboarding module: intake states keep HOME_VIEW order (sortValue = their index),
// Orientation comes after them. Themes inside other modules share a weekNbr so they fall back to
// alphabetical label sort (sortValue = 0).
export const ORIENTATION_THEME = {
  key: 'orientation',
  label: 'Orientation',
  sortValue: HOME_VIEW_ENROLLMENT_STATES.length,
} as const
export const POST_PROGRAM_THEME = { key: 'post-program', label: 'Post-Program', sortValue: 0 } as const

interface Module {
  moduleKey: string
  moduleLabel: string
  moduleSortValue: number
  themeKey: string
  themeLabel: string
  themeSortValue: number
  enrollmentState: EnrollmentState
}

export interface ThemeGroup {
  key: string
  label: string
  enrollmentState: EnrollmentState
  participants: HomeViewParticipantFragment[]
}

export interface ParticipantModule {
  moduleKey: string
  moduleLabel: string
  groups: ThemeGroup[]
}

const getWeekNbr = (programStartDate: Date | undefined | null, timezone?: string | null) =>
  MeruDate.now(timezone).formatWeekNumber(programStartDate)

const getWeekDayNbr = (programStartDate: Date | undefined | null, timezone?: string | null): number =>
  MeruDate.now(timezone).formatWeekDayNumber(programStartDate) ?? Number.MAX_SAFE_INTEGER

const participantName = (participant: HomeViewParticipantFragment) => {
  const { firstname, lastname } = participant
  return `${firstname ?? ''} ${lastname ?? ''}`.toLowerCase()
}

const participantIntakeCallTime = (participant: HomeViewParticipantFragment) => participant.intakeCallTime

export const getModule = (
  participant: Pick<HomeViewParticipantFragment, 'enrollment' | 'timezone'>,
  programs: ProgramsByID
): Module => {
  const { state } = participant.enrollment

  const intakeIndex = HOME_VIEW_ENROLLMENT_STATES.findIndex((entry) => entry.enrollmentState === state)
  if (intakeIndex === -1) {
    throw new Error(`sortParticipants: unexpected enrollment state ${state} — should not included in home view`)
  }

  // Intake states fall under Onboarding, one theme per state.
  if (state !== EnrollmentState.InProgram) {
    return {
      moduleKey: ONBOARDING_MODULE.key,
      moduleLabel: ONBOARDING_MODULE.label,
      moduleSortValue: ONBOARDING_MODULE.sortValue,
      themeKey: state,
      themeLabel: HOME_VIEW_ENROLLMENT_STATES[intakeIndex].label,
      themeSortValue: intakeIndex,
      enrollmentState: state,
    }
  }

  const weekNbr = getWeekNbr(participant.enrollment.programStartDate, participant.timezone) ?? 0
  const program = programs?.[participant.enrollment.programID]
  const lastWeekNbr = program?.lastWeekNbr ?? DEFAULT_LAST_WEEK_NBR

  // InProgram + program start date in the future → Orientation theme in Onboarding.
  if (weekNbr === 0) {
    return {
      moduleKey: ONBOARDING_MODULE.key,
      moduleLabel: ONBOARDING_MODULE.label,
      moduleSortValue: ONBOARDING_MODULE.sortValue,
      themeKey: ORIENTATION_THEME.key,
      themeLabel: ORIENTATION_THEME.label,
      themeSortValue: ORIENTATION_THEME.sortValue,
      enrollmentState: state,
    }
  }

  // Past the program's last week → Wrapping up module, Post-Program theme.
  if (weekNbr > lastWeekNbr) {
    return {
      moduleKey: WRAPPING_UP_MODULE.key,
      moduleLabel: WRAPPING_UP_MODULE.label,
      moduleSortValue: WRAPPING_UP_MODULE.sortValue,
      themeKey: POST_PROGRAM_THEME.key,
      themeLabel: POST_PROGRAM_THEME.label,
      themeSortValue: POST_PROGRAM_THEME.sortValue,
      enrollmentState: state,
    }
  }

  const themeLabel = getThemeLabel(weekNbr, lastWeekNbr, program?.weeks ?? {})
  return {
    moduleKey: `module-${weekNbr}`,
    moduleLabel: `Module ${weekNbr}`,
    moduleSortValue: weekNbr,
    themeKey: themeLabel,
    themeLabel,
    themeSortValue: 0,
    enrollmentState: state,
  }
}

const sortThemeParticipants = (
  themeKey: string,
  enrollmentState: EnrollmentState,
  participants: HomeViewParticipantFragment[],
  programs: ProgramsByID
): HomeViewParticipantFragment[] => {
  if (enrollmentState === EnrollmentState.IntakeCallScheduled) {
    return _.sortBy(participants, participantIntakeCallTime)
  }
  if (enrollmentState !== EnrollmentState.InProgram) {
    return _.sortBy(participants, participantName)
  }
  if (themeKey === POST_PROGRAM_THEME.key) {
    // Post-Program: treatment first (providerRole 'therapist'), then coaching;
    // within program, fresh graduates first (programStartDate desc); then by name asc.
    return _.orderBy(
      participants,
      [
        (p) => (programs?.[p.enrollment.programID]?.providerRole === 'therapist' ? 0 : 1),
        (p) => p.enrollment.programStartDate ?? new Date(0),
        participantName,
      ],
      ['asc', 'desc', 'asc']
    )
  }
  // In-program theme groups: by day-in-week (in each patient's timezone, matching the
  // day indicator shown in the list), then name.
  return _.sortBy(participants, [(p) => getWeekDayNbr(p.enrollment.programStartDate, p.timezone), participantName])
}

const sortParticipants = (
  participants: Array<HomeViewParticipantFragment>,
  programs: ProgramsByID
): ParticipantModule[] => {
  const withModule = participants.map((participant) => ({ participant, module: getModule(participant, programs) }))

  const byModuleKey = _.groupBy(withModule, ({ module: m }) => m.moduleKey)

  return _.chain(Object.values(byModuleKey))
    .map((moduleEntries) => {
      const moduleInfo = moduleEntries[0].module
      const byThemeKey = _.groupBy(moduleEntries, ({ module: m }) => m.themeKey)
      const groups: ThemeGroup[] = _.chain(Object.values(byThemeKey))
        .map((themeEntries) => {
          const themeInfo = themeEntries[0].module
          return {
            key: themeInfo.themeKey,
            label: themeInfo.themeLabel,
            enrollmentState: themeInfo.enrollmentState,
            themeSortValue: themeInfo.themeSortValue,
            participants: sortThemeParticipants(
              themeInfo.themeKey,
              themeInfo.enrollmentState,
              themeEntries.map((e) => e.participant),
              programs
            ),
          }
        })
        .sortBy([(g) => g.themeSortValue, (g) => g.label.toLowerCase()])
        .map(({ themeSortValue: _t, ...rest }) => rest)
        .value()
      return {
        moduleKey: moduleInfo.moduleKey,
        moduleLabel: moduleInfo.moduleLabel,
        moduleSortValue: moduleInfo.moduleSortValue,
        groups,
      }
    })
    .sortBy((m) => m.moduleSortValue)
    .map(({ moduleSortValue: _m, ...rest }) => rest)
    .value()
}

export default sortParticipants
